import React, { useState } from 'react';
import { Card, Timeline, Tag, Typography, Space, Button, Modal, Input, Spin, Empty, Alert, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { approvalService, type ApprovalActionLogDTO, type ApprovalInstanceStepDTO } from '@/services/approval.service';
import type { RejectionPayload } from '@/services/rejection.service';
import { RejectionReasonModal } from '@/components/rejection/RejectionReasonModal';
import { RejectionHistoryPanel } from '@/components/rejection/RejectionHistoryPanel';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import {
  approveViaDomainOrInstance,
  formatApproveSuccessMessage,
  rejectViaDomainOrInstance,
  revokeViaDomainOrInstance,
} from '@/services/approval-domain-actions';
import { CheckOutlined, CloseOutlined, UndoOutlined } from '@ant-design/icons';
import { AiApprovalBriefPanel } from '@/components/approval/AiApprovalBriefPanel';

const { Text, Paragraph } = Typography;

const ENTITY_PURCHASE_ORDER = 'purchase_order';

function stepStatusColor(status: string): string {
  const s = status.toLowerCase();
  if (s === 'approved') return 'green';
  if (s === 'pending') return 'blue';
  if (s === 'rejected') return 'red';
  if (s === 'revoked') return 'orange';
  if (s === 'waiting') return 'default';
  return 'default';
}

export interface ApprovalPanelProps {
  entityType: string;
  entityId: string;
  /** When set, approve/reject controls stay hidden and this message is shown (e.g. sales SO review not yet recorded). */
  approvalActionsBlockedReason?: string | null;
  /** Domain approve (forwards optional remarks); preferred over raw instance approve when document APIs exist. */
  onApproveViaDocument?: (remarks?: string) => Promise<void>;
  /** Domain reject — must send structured reason from {@link RejectionReasonModal}. */
  onRejectViaDocument?: (payload: RejectionPayload) => Promise<void>;
  /** @deprecated Use onRejectViaDocument */
  onRejectViaPo?: (payload: RejectionPayload) => Promise<void>;
  /** @deprecated Use onApproveViaDocument */
  onApproveViaPo?: (remarks?: string) => Promise<void>;
  /** Extra React Query keys to invalidate after approve/reject (e.g. [['purchase-orders'], ['purchase-order', id]]). */
  extraInvalidateQueryKeys?: ReadonlyArray<readonly unknown[]>;
  /** Show immutable rejection audit log for this document (default true). */
  showRejectionHistory?: boolean;
  /** When false, skip the AI Approval Brief (page mounts it separately). Default true. */
  showAiBrief?: boolean;
}

export const ApprovalPanel: React.FC<ApprovalPanelProps> = ({
  entityType,
  entityId,
  approvalActionsBlockedReason,
  onApproveViaDocument,
  onRejectViaDocument,
  onRejectViaPo,
  onApproveViaPo,
  extraInvalidateQueryKeys,
  showRejectionHistory = true,
  showAiBrief = true,
}) => {
  const queryClient = useQueryClient();
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [acting, setActing] = useState(false);

  const approveCb =
    onApproveViaDocument ?? (entityType === ENTITY_PURCHASE_ORDER ? onApproveViaPo : undefined);
  const rejectCb =
    onRejectViaDocument ?? (entityType === ENTITY_PURCHASE_ORDER ? onRejectViaPo : undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['approval-summary', entityType, entityId],
    queryFn: () => approvalService.getSummary(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });

  const instance = data?.instance;
  const history: ApprovalActionLogDTO[] = data?.history || [];
  const pendingApproverLabel = data?.pendingApproverLabel;
  const pendingApprovers = data?.pendingApprovers;
  const canCurrentUserAct = data?.canCurrentUserAct === true;
  const canRevoke = data?.can_revoke === true;
  const revokeBlockReasons = data?.revoke_block_reasons || [];
  const revokeAsOverride = data?.revoke_as_override === true;

  const invalidateAfterAction = async () => {
    await queryClient.invalidateQueries({ queryKey: ['approval-summary', entityType, entityId] });
    await queryClient.invalidateQueries({ queryKey: ['rejection-logs', entityType, entityId] });
    await queryClient.invalidateQueries({ queryKey: ['approval-inbox'] });
    await queryClient.invalidateQueries({ queryKey: ['ai-approval-brief', entityType, entityId] });
    for (const key of extraInvalidateQueryKeys ?? []) {
      if (key?.length) {
        await queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
    }
    if (entityType === ENTITY_PURCHASE_ORDER) {
      await queryClient.invalidateQueries({ queryKey: ['purchase-order'] });
    }
  };

  const handleApproveConfirm = async () => {
    if (!instance?.id) return;
    setActing(true);
    try {
      let successMsg = 'Approved';
      if (approveCb) {
        await approveCb(remarks || undefined);
        const summary = await approvalService.getSummary(entityType, entityId);
        const pending = summary.instance?.instanceSteps?.find((s) => s.status === 'pending');
        successMsg = formatApproveSuccessMessage({
          fullyApproved: summary.instance?.status === 'approved',
          status: summary.instance?.status,
          currentStepNumber: summary.instance?.current_step_number,
          totalSteps: summary.instance?.instanceSteps?.length,
          nextLevelName: pending
            ? `Step ${pending.step_number}${pending.level_name ? ` (${pending.level_name})` : ''}`
            : undefined,
        });
      } else {
        const result = await approveViaDomainOrInstance({
          entityType,
          entityId,
          instanceId: instance.id,
          remarks: remarks || undefined,
        });
        successMsg = formatApproveSuccessMessage(result);
      }
      message.success(successMsg);
      setApproveModalOpen(false);
      await invalidateAfterAction();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const handleRejectConfirm = async (payload: RejectionPayload) => {
    if (!instance?.id) return;
    setActing(true);
    try {
      if (rejectCb) {
        await rejectCb(payload);
      } else {
        await rejectViaDomainOrInstance({
          entityType,
          entityId,
          instanceId: instance.id,
          payload,
        });
      }
      message.success('Rejected');
      setRejectModalOpen(false);
      await invalidateAfterAction();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!instance?.id) return;
    const reason = revokeReason.trim();
    if (!reason) {
      message.error('A reason is required to revoke approval');
      return;
    }
    setActing(true);
    try {
      await revokeViaDomainOrInstance({
        entityType,
        entityId,
        instanceId: instance.id,
        reason,
      });
      message.success('Approval revoked — document returned to Draft');
      setRevokeModalOpen(false);
      setRevokeReason('');
      await invalidateAfterAction();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Revoke failed');
    } finally {
      setActing(false);
    }
  };

  const brief = showAiBrief ? (
    <AiApprovalBriefPanel
      entityType={entityType}
      entityId={entityId}
      defaultExpanded={false}
    />
  ) : null;

  if (isLoading) {
    return (
      <>
        {brief}
        <Card size="small" title="Approval">
          <Spin />
        </Card>
      </>
    );
  }

  if (!instance) {
    return (
      <>
        {brief}
        <Card size="small" title="Approval">
          <Empty description="No approval instance for this document" />
        </Card>
      </>
    );
  }

  const canAct = instance.status === 'pending_approval' && canCurrentUserAct;
  const blockedByExternalReason = !!approvalActionsBlockedReason && instance.status === 'pending_approval';
  const showPendingApprover =
    instance.status === 'pending_approval' && (pendingApproverLabel || pendingApprovers?.length);
  const steps: ApprovalInstanceStepDTO[] = instance.instanceSteps ?? [];

  return (
    <>
      {brief}
    <Card
      size="small"
      title={
        <Space direction="vertical" size={0}>
          <Space>
            <span>Universal approval</span>
            <Tag>{instance.status}</Tag>
            <Text type="secondary">
              Step {instance.current_step_number}
              {steps.length ? ` of ${steps.length}` : ''}
            </Text>
          </Space>
          {showPendingApprover ? (
            <PendingApproverText
              compact
              prefix="Awaiting approval from"
              pendingApproverLabel={pendingApproverLabel}
              pendingApprovers={pendingApprovers}
            />
          ) : null}
        </Space>
      }
      extra={
        <Space>
          {canAct && !blockedByExternalReason ? (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => {
                  setRemarks('');
                  setApproveModalOpen(true);
                }}
              >
                Approve
              </Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => setRejectModalOpen(true)}>
                Reject
              </Button>
            </>
          ) : null}
          {canRevoke ? (
            <Button
              size="small"
              icon={<UndoOutlined />}
              onClick={() => {
                setRevokeReason('');
                setRevokeModalOpen(true);
              }}
            >
              Revoke approval
            </Button>
          ) : null}
        </Space>
      }
    >
      {blockedByExternalReason && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={approvalActionsBlockedReason} />
      )}
      {instance.status === 'pending_approval' && !canCurrentUserAct && !blockedByExternalReason && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="You are not the assignee for the current approval step."
        />
      )}
      {showPendingApprover && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <PendingApproverText
              prefix="Awaiting approval from"
              pendingApproverLabel={pendingApproverLabel}
              pendingApprovers={pendingApprovers}
            />
          }
        />
      )}
      {instance.status === 'approved' && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fully approved"
          description={
            canRevoke && revokeAsOverride
              ? 'You have override permission to revoke this approval back to Draft.'
              : undefined
          }
        />
      )}
      {instance.status === 'approved' && !canRevoke && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Approval cannot be revoked by you"
          description={
            revokeBlockReasons.length > 0
              ? revokeBlockReasons.join('; ')
              : 'Only the final-step approver or a user with approval.revoke may revoke.'
          }
        />
      )}
      {instance.status === 'revoked' && (
        <Alert
          type="warning"
          showIcon
          message="Approval revoked"
          description="Document returned to Draft. Edit and submit again for a new approval instance."
          style={{ marginBottom: 12 }}
        />
      )}
      {instance.status === 'rejected' && (
        <Alert
          type="error"
          showIcon
          message="Rejected"
          description="Correct the document and submit again for approval."
          style={{ marginBottom: 12 }}
        />
      )}

      {steps.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Approval steps
          </Text>
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {steps.map((s) => (
              <div
                key={s.id || s.step_number}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '6px 10px',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  background: s.status === 'pending' ? '#f0f5ff' : undefined,
                }}
              >
                <Space wrap size={4}>
                  <Text strong>
                    Step {s.step_number}
                    {s.level_name ? ` — ${s.level_name}` : ''}
                  </Text>
                  <Tag color={stepStatusColor(s.status)}>{s.status}</Tag>
                </Space>
                {s.action_taken ? <Text type="secondary">{s.action_taken}</Text> : null}
              </div>
            ))}
          </Space>
        </div>
      )}

      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Instance <Text code>{instance.id}</Text>
      </Paragraph>
      <Timeline
        items={(history || []).map((h) => ({
          children: (
            <div>
              <Space wrap size={4}>
                <Tag>{h.action_taken}</Tag>
                {h.actor_full_name ? <Text strong>{h.actor_full_name}</Text> : null}
                <Text type="secondary">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</Text>
              </Space>
              {h.remarks ? <div style={{ marginTop: 4 }}>{h.remarks}</div> : null}
            </div>
          ),
        }))}
      />
      {showRejectionHistory ? (
        <div style={{ marginTop: 16 }}>
          <RejectionHistoryPanel documentType={entityType} documentId={entityId} />
        </div>
      ) : null}

      <Modal
        title="Approve"
        open={approveModalOpen}
        onOk={handleApproveConfirm}
        onCancel={() => setApproveModalOpen(false)}
        confirmLoading={acting}
        okText="Approve"
      >
        <Input.TextArea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" />
      </Modal>

      <Modal
        title="Revoke approval"
        open={revokeModalOpen}
        onOk={handleRevokeConfirm}
        onCancel={() => setRevokeModalOpen(false)}
        confirmLoading={acting}
        okText="Revoke"
        okButtonProps={{ danger: true }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            revokeAsOverride
              ? 'You are revoking as an override (approval.revoke). The document will return to Draft.'
              : 'The document will return to Draft so it can be edited and resubmitted.'
          }
        />
        <Input.TextArea
          rows={3}
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.target.value)}
          placeholder="Reason for revoke (required)"
        />
      </Modal>

      <RejectionReasonModal
        open={rejectModalOpen}
        title="Reject"
        documentType={entityType}
        documentLabel={instance.document_number ? `Document ${instance.document_number}` : undefined}
        confirmLoading={acting}
        onCancel={() => setRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
      />
    </Card>
    </>
  );
};
